/**
 * Project paths are forward-slash paths relative to the project root, such as
 * `assets/level1.pack.json`. They double as URLs the game loads, so they must
 * never escape the root.
 */

export class ProjectPathError extends Error {
  constructor(public readonly path: string, reason: string) {
    super(`Invalid project path "${path}": ${reason}`);
    this.name = 'ProjectPathError';
  }
}

/** Normalises a project path, throwing if it is absolute or leaves the project root. */
export function normalizeProjectPath(path: string): string {
  if (typeof path !== 'string' || path.trim() === '') throw new ProjectPathError(String(path), 'empty');
  if (path.includes('\\')) throw new ProjectPathError(path, 'use forward slashes');
  if (path.includes('\0')) throw new ProjectPathError(path, 'contains a null byte');
  if (path.startsWith('/') || /^[A-Za-z]:/.test(path) || /^[a-z][a-z0-9+.-]*:\/\//i.test(path)) {
    throw new ProjectPathError(path, 'must be relative to the project root');
  }
  const out: string[] = [];
  for (const part of path.split('/')) {
    if (part === '' || part === '.') continue;
    if (part === '..') {
      if (out.length === 0) throw new ProjectPathError(path, 'leaves the project root');
      out.pop();
    } else {
      out.push(part);
    }
  }
  if (out.length === 0) throw new ProjectPathError(path, 'points at the project root');
  return out.join('/');
}

export function isValidProjectPath(path: string): boolean {
  try {
    normalizeProjectPath(path);
    return true;
  } catch {
    return false;
  }
}

/** The folder part of a project path (`''` for a file at the root). */
export function dirname(path: string): string {
  const i = path.lastIndexOf('/');
  return i < 0 ? '' : path.slice(0, i);
}

/**
 * Resolves `relative` against the folder of `fromFile`, as Tiled does for a
 * tileset image path. The result must stay inside the project root.
 */
export function resolveFrom(fromFile: string, relative: string): string {
  const base = dirname(fromFile);
  return normalizeProjectPath(base ? `${base}/${relative}` : relative);
}
